import { css, html } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import Sortable, { type SortableEvent, Swap as SwapPlugin } from 'sortablejs'
import { LitHauntedElement } from '../mixins/lit-haunted-element';
import { useEffect } from 'haunted';
import { differenceWith, isEqual } from 'lodash'

Sortable.mount(new SwapPlugin())
export interface EPSortableTreeNode {
  id: string;
  parentID: string | null;
  index: number;
}

export interface EPSortableUpdateEvent {
  changedItems: EPSortableTreeNode[];
  rawEvent: SortableEvent;
}

@customElement('ep-sortable')
export class EPSortable extends LitHauntedElement {

  @property({ attribute: 'id-attr-name', reflect: true })
  dataIdAttrName?: string;
  
  @property({ attribute: 'nested-item-selector', reflect: true })
  nestedItemSelector: string = '[data-nested-sortable]';

  @property({ type: Boolean, attribute: 'disable-sorting', reflect: true })
  disableSorting: boolean = false;

  @property({ type: Boolean, attribute: 'swap-mode', reflect: true })
  swapModeEnabled: boolean = false;

  @property({ type: Boolean, reflect: true })
  nested: boolean = false;

  @state()
  sortableInstances: Sortable[] = [];

  @state()
  _lastItems: EPSortableTreeNode[] = [];

  @query('slot')
  slotElement?: HTMLSlotElement;

  render() {
    useEffect(() => {
      for(let inst of this.sortableInstances) {
        //inst.options.sort = !this.disableSorting;
        inst.options.disabled = this.disableSorting;
        for(let child of inst.el.children) {
          if (this.disableSorting) {
            child.classList.add('sortable-disabled')
          }
          else {
            child.classList.remove('sortable-disabled')
          }
        }
      }
    }, [this.sortableInstances, this.disableSorting])
    useEffect(() => {
      this._lastItems = this.getItems();
    }, [])

    return html`
      <slot @slotchange=${() => this.requestUpdate()}></slot>
    `
  }

  private _initSortable() {
    console.debug(`[${this.tagName}] Initialize Sortable`);
    const self = this;
    if (this.nested) {
      // Get nestable children
      const nestableChildren: HTMLElement[] = [this, ...Array.from(this.querySelectorAll(this.nestedItemSelector))]
        .filter(e => e instanceof HTMLElement)
        .map(e => e as HTMLElement);
      // Initialize SortableJS
      this.sortableInstances = [
        ...(nestableChildren.map(e => new Sortable(e, {
          group: 'nested',
          animation: 150,
          fallbackOnBody: true,
          swapThreshold: 0.65,
          onUpdate: (event) => this._dispatchOrderChanged.bind(self)(event),
          onAdd: (event) => this._dispatchOrderChanged.bind(self)(event),
          //onRemove: (event) => this._dispatchOrderChanged.bind(self)(event),
          dataIdAttr: this.dataIdAttrName,
          swap: this.swapModeEnabled,
        })))
      ];
    }
    else {
      this.sortableInstances = [
        new Sortable(this, {
          animation: 150,
          onUpdate: (event) => this._dispatchOrderChanged.bind(self)(event),
          onAdd: (event) => this._dispatchOrderChanged.bind(self)(event),
          //onRemove: (event) => this._dispatchOrderChanged.bind(self)(event),
          dataIdAttr: this.dataIdAttrName,
          swap: this.swapModeEnabled,
        })
      ];
    }

    // If we recycle this, it may be because items were added after <ep-sortable> was initialized.
    setTimeout(() => {
      this._lastItems = this.getItems();
    }, 20);
  }

  private _destroySortable() {
    console.debug(`[${this.tagName}] Destroy Sortable`);
    for(let inst of this.sortableInstances) {
      inst.destroy();
    }
    this.sortableInstances = [];
  }

  public recycleSortable() {
    this._destroySortable();
    this._initSortable();
  }

  public override connectedCallback() {
    super.connectedCallback();
    this._initSortable();
  }

  public override disconnectedCallback() {
    super.disconnectedCallback();
    this._destroySortable();
  }

  public getItems(): EPSortableTreeNode[] {
    return this.sortableInstances
      .map(inst => inst.toArray().map<EPSortableTreeNode>((id, index) => ({
        id: id,
        parentID: inst.el.closest(`[${this.dataIdAttrName}]`)?.getAttribute(this.dataIdAttrName ?? '') ?? null,
        index,
      })))
      .flat(2);
  }
  
  private _dispatchOrderChanged(sortableEvent: SortableEvent) {
    //console.log('sortable?', sortableEvent);
    // Get the current items
    const currentItems = this.getItems();
    // Compare the current items with the last items using deep equality
    const diff = differenceWith(currentItems, this._lastItems, isEqual);
    const augmentedDiff = diff.map(e => ({
      ...e,
      previousIndex: this._lastItems.find(x => x.id === e.id)?.index,
      previousParentID: this._lastItems.find(x => x.id === e.id)?.parentID,
    }));
    // Update the last items
    this._lastItems = this.getItems();
    // Compose and dispatch the event
    const event = new CustomEvent<EPSortableUpdateEvent>('ep-sortable-update', { 
      bubbles: true,
      composed: true,
      detail: {
        changedItems: augmentedDiff, // diff,
        rawEvent: sortableEvent,
      },
    });
    this.dispatchEvent(event);
  }

  static styles = css`
    :host {
      display: contents;
    }
  `
}

declare global {
  interface HTMLElementTagNameMap {
    'ep-sortable': EPSortable
  }
}
